import { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingDown, AlertTriangle, Info } from "lucide-react";

type BannerType = "info" | "savings_opportunity" | "warning";

interface InfoBannerProps {
  /** Type of banner determining styling and icon */
  type: BannerType;
  /** Message content to display */
  message: string | ReactNode;
}

/**
 * InfoBanner Component
 *
 * Cross-field validation and information banner with animated entrance/exit.
 *
 * Types:
 * - `savings_opportunity`: Green banner with TrendingDown icon for cost-saving insights
 * - `warning`: Amber banner with AlertTriangle icon for validation warnings
 *
 * Uses framer-motion AnimatePresence for smooth animations.
 *
 * @example
 * ```tsx
 * <InfoBanner
 *   type="savings_opportunity"
 *   message="You could save $240/year by bundling with renters insurance"
 * />
 *
 * <InfoBanner
 *   type="warning"
 *   message="Your vehicle value exceeds recommended coverage limits"
 * />
 * ```
 */
export function InfoBanner({ type, message }: InfoBannerProps) {
  const bannerClass =
    type === "warning"
      ? "info-banner-warning"
      : type === "info"
        ? "info-banner-info"
        : "info-banner-savings";
  const Icon = type === "warning" ? AlertTriangle : type === "info" ? Info : TrendingDown;
  const iconColor =
    type === "warning"
      ? "text-amber-600"
      : type === "info"
        ? "text-slate-600"
        : "text-green-600";

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className={`${bannerClass} flex items-start gap-3`}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        role={type === "warning" ? "alert" : "status"}
      >
        <Icon size={18} className={`flex-shrink-0 mt-0.5 ${iconColor}`} />
        <div className="flex-1">{message}</div>
      </motion.div>
    </AnimatePresence>
  );
}
